import { useState, useCallback } from 'react';
import { Check, Copy, MessageCircleQuestionMark, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button, ICON_SIZE } from '../design-system';
import { openFeedbackWidget } from '../lib/featurebase';

/**
 * Copy text to clipboard with fallback for older browsers.
 * Returns true on success.
 */
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback: textarea + execCommand
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
}

/**
 * Match raw error text to a human-readable cause (errors.causes.*). Ordered
 * most-specific first; the raw text itself never renders — users get plain
 * language, bug reports get the original via Copy.
 */
function classifyError(text) {
  const t = text || '';
  if (/organization_on_hold|organization (has been|is) (disabled|blocked)|api key (expired|revoked)/i.test(t)) {
    return 'providerUnavailable';
  }
  if (/rate.?limit|429|too many requests/i.test(t)) {
    return 'rateLimited';
  }
  if (/401|unauthorized|invalid api key|authentication/i.test(t)) {
    return 'authDenied';
  }
  if (/timeout|timed?.?out|network|failed to fetch|connection|ECONN/i.test(t)) {
    return 'network';
  }
  return 'generic';
}

/**
 * The one error note (visible human-readable message + actions — no
 * disclosure; failure info never hides behind a click). Copy always carries
 * the full original text with stack so a pasted report stays debuggable, and
 * answers with a Copy→Check swap. Optional title renders the red-icon row for
 * surfaces that have no heading of their own; onRetry adds the explicit
 * retry action alongside Copy and Feedback.
 */
export default function ErrorDetails({ error, title, onRetry, retryDisabled = false }) {
  const { t } = useTranslation(['common', 'sidebar']);
  const [copied, setCopied] = useState(false);

  const errorText = typeof error === 'string'
    ? error
    : `${error?.toString() || 'Unknown error'}${error?.stack ? `\n\n${error.stack}` : ''}`;

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(errorText);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [errorText]);

  return (
    <div className="rds-error-note">
      {title && (
        <p className="rds-error-note__title">
          <XCircle
            size={ICON_SIZE.sm}
            aria-hidden="true"
            className="rds-error-note__icon"
          />
          {title}
        </p>
      )}
      <p className="rds-error-note__message">
        {t(`errors.causes.${classifyError(errorText)}`)}
      </p>
      <div className="rds-error-note__actions">
        <Button
          variant="neutral"
          size="sm"
          className="rds-error-note__secondary"
          aria-label={copied ? t('errors.copied', 'Copied!') : t('errors.copyError', 'Copy details')}
          title={copied ? t('errors.copied', 'Copied!') : t('errors.copyError', 'Copy details')}
          onClick={handleCopy}
          startIcon={copied ? <Check size={ICON_SIZE.xs} /> : <Copy size={ICON_SIZE.xs} />}
        >
          {copied
            ? t('errors.copied', 'Copied!')
            : t('errors.copyError', 'Copy details')}
        </Button>
        <Button
          variant="neutral"
          size="sm"
          className="rds-error-note__secondary"
          aria-label={t('sidebar:footer.feedback')}
          title={t('sidebar:footer.feedback')}
          onClick={() => openFeedbackWidget()}
          startIcon={<MessageCircleQuestionMark size={ICON_SIZE.xs} />}
        >
          {t('sidebar:footer.feedback')}
        </Button>
        {onRetry && (
          <Button
            variant="primary"
            size="sm"
            className="rds-error-note__retry"
            onClick={onRetry}
            disabled={retryDisabled}
          >
            {t('button.retry')}
          </Button>
        )}
      </div>
    </div>
  );
}
